import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AgentConfig, AgentSuggestion, AgentAutonomyLevel, AgentScope } from '../../../types';
import { getAgentConfig, createAgentConfig, updateAgentConfig, getAgentSuggestions, resolveAgentSuggestion } from '../../../services/agentService';

interface Props { pca: IPublicClientApplication; projectId: string; }

const SCOPE_LABEL: Record<number, string> = { 100000000: 'Project Wide', 100000001: 'Personal Only' };

export default function AIAgentTab({ pca, projectId }: Props) {
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [draft, setDraft]   = useState<AgentConfig | null>(null);
  const [suggestions, setSuggestions] = useState<AgentSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  const reload = () => Promise.all([getAgentConfig(pca, projectId), getAgentSuggestions(pca, projectId)])
    .then(([c, s]) => { setConfig(c); setDraft(c); setSuggestions(s); })
    .finally(() => setLoading(false));
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  const handleConfigure = async () => {
    setLoading(true);
    await createAgentConfig(pca, projectId);
    reload();
  };

  const handleSave = async () => {
    if (!draft) return;
    await updateAgentConfig(pca, draft.dagrun_pm_agentconfigid, {
      dagrun_pm_autonomylevel: draft.dagrun_pm_autonomylevel,
      dagrun_pm_isenabled: draft.dagrun_pm_isenabled,
      dagrun_pm_atriskthresholdoverduetasks: draft.dagrun_pm_atriskthresholdoverduetasks,
      dagrun_pm_atriskthresholdbudgetpct: draft.dagrun_pm_atriskthresholdbudgetpct,
      dagrun_pm_askdagruntmscope: draft.dagrun_pm_askdagruntmscope,
    });
    setMsg('Saved');
    reload();
  };

  const handleResolve = async (s: AgentSuggestion, accept: boolean) => {
    await resolveAgentSuggestion(pca, s.dagrun_pm_agentsuggestionid, accept);
    setSuggestions(suggestions.filter(x => x.dagrun_pm_agentsuggestionid !== s.dagrun_pm_agentsuggestionid));
  };

  if (loading) return <div style={{ padding: '24px 32px' }}><div className="spinner" /></div>;

  if (!config) return (
    <div style={{ padding: '24px 32px' }}>
      <div className="card" style={{ padding: 48, textAlign: 'center', maxWidth: 560, margin: '40px auto' }}>
        <div className="eyebrow">AI AGENT</div>
        <h3 style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 16, color: 'var(--ink-2)', marginTop: 12 }}>
          AI Agent not configured
        </h3>
        <p style={{ fontWeight: 300, fontSize: 14, color: 'var(--ink-3)', marginTop: 8 }}>
          Configure the agent to enable autonomous task assignment, risk flagging, and proactive suggestions.
        </p>
        <button className="btn-primary" style={{ marginTop: 16 }} onClick={handleConfigure}>Configure Agent</button>
      </div>
    </div>
  );

  if (!draft) return null;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 760 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3>AI Agent</h3>
      </div>

      {/* Config */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>CONFIGURATION</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label>Autonomy Level</label>
            <select value={draft.dagrun_pm_autonomylevel} onChange={e => setDraft({ ...draft, dagrun_pm_autonomylevel: Number(e.target.value) })}>
              <option value={AgentAutonomyLevel.Advisory}>Advisory</option>
              <option value={AgentAutonomyLevel.Supervised}>Supervised</option>
              <option value={AgentAutonomyLevel.Autonomous}>Autonomous</option>
            </select>
          </div>
          <div>
            <label>Ask Dagrun Scope</label>
            <select value={draft.dagrun_pm_askdagruntmscope} onChange={e => setDraft({ ...draft, dagrun_pm_askdagruntmscope: Number(e.target.value) })}>
              <option value={AgentScope.ProjectWide}>{SCOPE_LABEL[AgentScope.ProjectWide]}</option>
              <option value={AgentScope.PersonalOnly}>{SCOPE_LABEL[AgentScope.PersonalOnly]}</option>
            </select>
          </div>
          <div>
            <label>At-risk threshold: overdue tasks</label>
            <input type="number" value={draft.dagrun_pm_atriskthresholdoverduetasks ?? 0}
              onChange={e => setDraft({ ...draft, dagrun_pm_atriskthresholdoverduetasks: Number(e.target.value) })} />
          </div>
          <div>
            <label>At-risk threshold: budget %</label>
            <input type="number" step="1" value={draft.dagrun_pm_atriskthresholdbudgetpct ?? 0}
              onChange={e => setDraft({ ...draft, dagrun_pm_atriskthresholdbudgetpct: Number(e.target.value) })} />
          </div>
        </div>
        <label style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={draft.dagrun_pm_isenabled} onChange={e => setDraft({ ...draft, dagrun_pm_isenabled: e.target.checked })} />
          Agent enabled
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button className="btn-primary" onClick={handleSave}>Save</button>
          {msg && <span style={{ color: 'var(--success)', fontSize: 13 }}>{msg}</span>}
        </div>
      </div>

      {/* Suggestions */}
      <div className="card" style={{ padding: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>PENDING SUGGESTIONS ({suggestions.length})</div>
        {suggestions.length === 0 ? (
          <div style={{ fontWeight: 300, fontSize: 14, color: 'var(--ink-3)' }}>No pending suggestions.</div>
        ) : (
          suggestions.map(s => (
            <div key={s.dagrun_pm_agentsuggestionid} style={{ padding: 12, border: '1px solid var(--border-light)', borderRadius: 8, marginBottom: 8, background: 'var(--surface-2)' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4, fontFamily: 'var(--font-mono)' }}>
                type {s.dagrun_pm_suggestiontype} · {s.dagrun_pm_entitytitle ?? '(no entity)'}
              </div>
              <div style={{ fontSize: 14, color: 'var(--ink)', marginBottom: 10, fontWeight: 300 }}>{s.dagrun_pm_suggestiontext}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-primary" onClick={() => handleResolve(s, true)}>Accept</button>
                <button className="btn-secondary" onClick={() => handleResolve(s, false)}>Reject</button>
              </div>
            </div>
          ))
        )}
      </div>

      <p style={{ marginTop: 20, color: 'var(--ink-4)', fontSize: 12, fontWeight: 300, fontStyle: 'italic' }}>
        Dagrun Assistant — task assignment and Loop component output require the AI Agent module to be configured in Copilot Studio by your tenant admin.
      </p>
    </div>
  );
}
